import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import logo from './logo.svg';
import logoMin from './logoMin.svg';
import nettbil from './nettbil.svg';
import biosort from './biosort-new.webp';
import bgImage from './bg.webp';
import { useLanguage } from './useLanguage';
import './App.css';

const App = () => {
  const { lang, t } = useLanguage();

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') {
      document.querySelectorAll('.animate-in').forEach((el) => el.classList.add('visible'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -30px 0px' }
    );

    document.querySelectorAll('.animate-in').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="App">
      <header className="hero-fullscreen">
        <div className="hero-background" aria-hidden="true">
          <div className="bg-png-overlay" style={{ backgroundImage: `url(${bgImage})` }}></div>
          <svg className="wave-animation" viewBox="0 0 1000 1000" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <filter id="blur">
                <feGaussianBlur in="SourceGraphic" stdDeviation="2" />
              </filter>
            </defs>
            <g className="wave-group-1" filter="url(#blur)">
              <circle cx="300" cy="500" r="150" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="0.5" className="wave-1" />
              <circle cx="300" cy="500" r="300" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="0.3" className="wave-2" />
            </g>
            <g className="wave-group-2" filter="url(#blur)">
              <circle cx="800" cy="600" r="200" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="0.4" className="wave-3" />
              <circle cx="800" cy="600" r="400" fill="none" stroke="rgba(255,255,255,0.03)" strokeWidth="0.2" className="wave-4" />
            </g>
          </svg>
        </div>
        <a href="https://flowlab.no" className="logo-link" aria-label={t('hero.logoAriaLabel')}>
          <img src={logo} alt="Flow Lab" className="top-left-logo logo-desktop" width="150" height="45" />
          <img src={logoMin} alt="Flow Lab" className="top-left-logo logo-mobile" width="150" height="45" />
        </a>
        <nav className="lang-switcher">
          <Link
            to="/"
            className={lang === 'no' ? 'active' : ''}
            onClick={() => localStorage.setItem('lang-preference', 'no')}
          >
            NO
          </Link>
          <span className="lang-divider">|</span>
          <Link
            to="/en/"
            className={lang === 'en' ? 'active' : ''}
            onClick={() => localStorage.setItem('lang-preference', 'en')}
          >
            EN
          </Link>
        </nav>
        <section className="hero-content">
          <h1>{t('hero.title')}</h1>
          <p>{t('hero.subtitle')}</p>
          <a href="mailto:kontakt@flowlab.no" className="cta-button">{t('hero.cta')}</a>
        </section>
        <section className="trusted-by" aria-label={t('trusted.sectionAria')}>
          <h2>{t('trusted.title')}</h2>
          <div className="logos">
            <a href="https://nettbil.no" target="_blank" rel="noopener noreferrer" aria-label={t('trusted.nettbilAria')}>
              <img src={nettbil} alt="Nettbil" className="nettbil-logo" width="130" height="22" loading="lazy" />
            </a>
            <a href="https://biosort.no" target="_blank" rel="noopener noreferrer" aria-label={t('trusted.biosortAria')}>
              <img src={biosort} alt="Biosort" width="120" height="40" loading="lazy" />
            </a>
          </div>
        </section>
      </header>

      <section className="services">
        <div className="section-inner">
          <h2 className="animate-in">{t('services.title')}</h2>
          <p className="section-subtitle animate-in" style={{ transitionDelay: '0.08s' }}>{t('services.subtitle')}</p>
          <div className="services-grid">
            <div className="service-card animate-in" style={{ transitionDelay: '0.1s' }}>
              <span className="service-card-number">01</span>
              <h3>{t('services.agent.title')}</h3>
              <p>{t('services.agent.desc')}</p>
              <span className="service-example">{t('services.agent.example')}</span>
            </div>
            <div className="service-card animate-in" style={{ transitionDelay: '0.2s' }}>
              <span className="service-card-number">02</span>
              <h3>{t('services.data.title')}</h3>
              <p>{t('services.data.desc')}</p>
              <span className="service-example">{t('services.data.example')}</span>
            </div>
            <div className="service-card animate-in" style={{ transitionDelay: '0.3s' }}>
              <span className="service-card-number">03</span>
              <h3>{t('services.productivity.title')}</h3>
              <p>{t('services.productivity.desc')}</p>
              <span className="service-example">{t('services.productivity.example')}</span>
            </div>
          </div>
        </div>
      </section>

      <section className="value-prop">
        <div className="section-inner">
          <h2 className="animate-in">{t('value.title')}</h2>
          <p className="section-subtitle animate-in" style={{ transitionDelay: '0.08s' }}>{t('value.subtitle')}</p>
          <p className="value-body animate-in" style={{ transitionDelay: '0.12s' }}>{t('value.body')}</p>
          <div className="differentiators">
            <div className="diff-item animate-in" style={{ transitionDelay: '0.15s' }}>
              <span className="diff-label">{t('value.diff1.label')}</span>
              <span className="diff-desc">{t('value.diff1.desc')}</span>
            </div>
            <div className="diff-item animate-in" style={{ transitionDelay: '0.25s' }}>
              <span className="diff-label">{t('value.diff2.label')}</span>
              <span className="diff-desc">{t('value.diff2.desc')}</span>
            </div>
            <div className="diff-item animate-in" style={{ transitionDelay: '0.35s' }}>
              <span className="diff-label">{t('value.diff3.label')}</span>
              <span className="diff-desc">{t('value.diff3.desc')}</span>
            </div>
          </div>
          <a href="mailto:kontakt@flowlab.no" className="cta-button cta-secondary animate-in" style={{ transitionDelay: '0.4s' }}>{t('value.cta')}</a>
        </div>
      </section>

      <section className="results">
        <div className="section-inner">
          <h2 className="animate-in">{t('results.title')}</h2>
          <div className="case-studies-grid">
            <div className="case-study animate-in" style={{ transitionDelay: '0.1s' }}>
              <h3>{t('results.case.title')}</h3>
              <p>{t('results.case.quote')}</p>
              <a href="https://vend.com/news/interim-report-q2-2025" target="_blank" rel="noopener noreferrer" className="case-study-link">
                {t('results.case.link')}
              </a>
            </div>
            <div className="case-study animate-in" style={{ transitionDelay: '0.18s' }}>
              <h3>{t('results.dnev.title')}</h3>
              <p>{t('results.dnev.quote')}</p>
              <a href="https://www.dnev.no" target="_blank" rel="noopener noreferrer" className="case-study-link">
                {t('results.dnev.link')}
              </a>
            </div>
          </div>
          <div className="metrics-bar">
            <div className="metric animate-in" style={{ transitionDelay: '0.15s' }}>
              <span className="metric-value">15+</span>
              <span className="metric-label">{t('results.metric1.label')}</span>
            </div>
            <div className="metric animate-in" style={{ transitionDelay: '0.25s' }}>
              <span className="metric-value">85%</span>
              <span className="metric-label">{t('results.metric2.label')}</span>
            </div>
            <div className="metric animate-in" style={{ transitionDelay: '0.35s' }}>
              <span className="metric-value">4.2x</span>
              <span className="metric-label">{t('results.metric3.label')}</span>
            </div>
          </div>
        </div>
      </section>

      <section className="about">
        <div className="section-inner">
          <h2 className="animate-in">{t('about.title')}</h2>
          <p className="animate-in" style={{ transitionDelay: '0.1s' }}>{t('about.p1')}</p>
          <p className="animate-in" style={{ transitionDelay: '0.18s' }}>{t('about.p2')}</p>
        </div>
      </section>

      <section className="final-cta">
        <div className="section-inner">
          <h2 className="animate-in">{t('cta.title')}</h2>
          <p className="animate-in" style={{ transitionDelay: '0.1s' }}>{t('cta.subtitle')}</p>
          <a href="mailto:kontakt@flowlab.no" className="cta-button animate-in" style={{ transitionDelay: '0.2s' }}>{t('cta.button')}</a>
        </div>
      </section>

      <footer className="footer">
        <div className="footer-content">
          <address className="company-info">
            <p>Flow Lab AS &bull; Oslo &bull; <a href="tel:+4745064756">+47 450 64 756</a> &bull; <a href="mailto:kontakt@flowlab.no">kontakt@flowlab.no</a></p>
          </address>
        </div>
      </footer>
    </div>
  );
};

export default App;
